/**
 * Pretext Homepage Integration — Sim Coaches
 * 
 * Uses Pretext (chenglou/pretext) for pixel-perfect text measurement
 * across every homepage section. No more CSS guesswork on mobile.
 *
 * What this does:
 * 1. Measures text BEFORE rendering to find optimal font sizes
 * 2. Prevents awkward line breaks on mobile
 * 3. Auto-fits headings to their containers
 * 4. Eliminates layout shift from text wrapping
 * 5. Graceful degradation — CSS still works if JS fails
 */

(function() {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────
  var CONFIG = {
    hero: { heading: { min: 24, max: 64, lh: 1.1 }, subheading: { min: 14, max: 28, lh: 1.4 } },
    carousel: { heading: { min: 20, max: 48, lh: 1.1 }, pricing: { min: 12, max: 20, lh: 1.4 }, legal: { min: 10, max: 14, lh: 1.4 } },
    stats: { number: { min: 18, max: 40, lh: 1.2 }, label: { min: 12, max: 16, lh: 1.4 } },
    dual: { heading: { min: 18, max: 36, lh: 1.2 }, desc: { min: 13, max: 18, lh: 1.6 } },
    finder: { heading: { min: 20, max: 40, lh: 1.2 }, desc: { min: 13, max: 18, lh: 1.5 } },
    rental: { heading: { min: 18, max: 36, lh: 1.2 }, desc: { min: 13, max: 18, lh: 1.5 } },
    seo: { heading: { min: 18, max: 32, lh: 1.2 }, title: { min: 14, max: 22, lh: 1.3 }, text: { min: 13, max: 16, lh: 1.6 } },
    btn: { min: 11, max: 16 },
    resizeDebounce: 150,
  };

  /**
   * Container-aware breakpoints — decisions based on ELEMENT width, not viewport.
   * This is what makes Pretext actually useful vs CSS media queries.
   */
  function isNarrow(el) {
    // Element's container is narrow (like a card, sidebar, or phone-width column)
    return getContentWidth(el) < 400;
  }
  function isMedium(el) {
    return getContentWidth(el) >= 400 && getContentWidth(el) < 700;
  }
  function maxLinesFor(el, narrowLines, wideLines) {
    // More lines allowed when container is narrow (text wraps more)
    var w = getContentWidth(el);
    if (w < 350) return narrowLines + 1; // extra tight
    if (w < 500) return narrowLines;
    return wideLines;
  }

  // ── Utilities ──────────────────────────────────────────────────────────

  function getFontString(el, overrideSize) {
    var cs = getComputedStyle(el);
    var size = overrideSize ? overrideSize + 'px' : cs.fontSize;
    return cs.fontWeight + ' ' + size + ' ' + cs.fontFamily;
  }

  function getContentWidth(el) {
    var cs = getComputedStyle(el);
    return el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
  }

  /**
   * Binary search for largest font size where text fits in maxLines.
   */
  function fitText(text, el, minPx, maxPx, lhRatio, maxLines) {
    if (!text || !el) return null;
    var w = getContentWidth(el);
    if (w <= 0) return null;

    maxLines = maxLines || 2;
    var lo = minPx, hi = maxPx, best = minPx;
    var P = window.Pretext;

    while (lo <= hi) {
      var mid = Math.round((lo + hi) / 2);
      var font = getFontString(el, mid);
      var lh = Math.round(mid * lhRatio);
      var prepared = P.prepare(text, font);
      var result = P.layout(prepared, w, lh);

      if (result.lineCount <= maxLines) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  function applySize(el, sizePx) {
    if (!el || !sizePx) return;
    el.style.fontSize = sizePx + 'px';
    el.setAttribute('data-pretext-fitted', sizePx);
  }

  function fitButton(btn) {
    var text = btn.textContent.trim();
    if (!text) return;
    var size = fitText(text, btn, CONFIG.btn.min, CONFIG.btn.max, 1.2, 1);
    if (size) applySize(btn, size);
  }

  function qsa(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function qs(sel) { return document.querySelector(sel); }

  // ── Section: Hero Carousel ─────────────────────────────────────────────

  function processHero() {
    qsa('.hero-slide').forEach(function(slide) {
      var h = slide.querySelector('.hero-slide__heading');
      var s = slide.querySelector('.hero-slide__subheading');
      var c = CONFIG.hero;

      if (h) {
        var size = fitText(h.textContent.trim(), h, c.heading.min, c.heading.max, c.heading.lh, maxLinesFor(h, 3, 2));
        if (size) applySize(h, size);
      }
      if (s) {
        var size = fitText(s.textContent.trim(), s, c.subheading.min, c.subheading.max, c.subheading.lh, maxLinesFor(s, 4, 2));
        if (size) applySize(s, size);
      }
      slide.querySelectorAll('.btn').forEach(fitButton);
    });
  }

  // ── Section: Tesla Vehicle Carousel (both instances) ───────────────────

  function processCarousels() {
    var c = CONFIG.carousel;
    qsa('.tesla-carousel__card').forEach(function(card) {
      var heading = card.querySelector('.tesla-carousel__heading');
      var pricing = card.querySelector('.tesla-carousel__pricing');
      var legal = card.querySelector('.tesla-carousel__legal');

      if (heading) {
        var size = fitText(heading.textContent.trim(), heading, c.heading.min, c.heading.max, c.heading.lh, maxLinesFor(heading, 2, 1));
        if (size) applySize(heading, size);
      }
      if (pricing) {
        // Pricing must always be 1 line regardless of container
        var size = fitText(pricing.textContent.trim(), pricing, c.pricing.min, c.pricing.max, c.pricing.lh, 1);
        if (size) applySize(pricing, size);
      }
      if (legal) {
        var size = fitText(legal.textContent.trim(), legal, c.legal.min, c.legal.max, c.legal.lh, maxLinesFor(legal, 3, 2));
        if (size) applySize(legal, size);
      }
      // Buttons
      card.querySelectorAll('.tesla-carousel__btn').forEach(fitButton);
    });
  }

  // ── Section: Tesla Dual Content ────────────────────────────────────────

  function processDualContent() {
    var c = CONFIG.dual;
    qsa('.tesla-dual-content__item').forEach(function(item) {
      var h = item.querySelector('.tesla-dual-content__heading');
      var d = item.querySelector('.tesla-dual-content__description');

      if (h) {
        var size = fitText(h.textContent.trim(), h, c.heading.min, c.heading.max, c.heading.lh, maxLinesFor(h, 2, 1));
        if (size) applySize(h, size);
      }
      if (d) {
        var size = fitText(d.textContent.trim(), d, c.desc.min, c.desc.max, c.desc.lh, maxLinesFor(d, 6, 4));
        if (size) applySize(d, size);
      }
      item.querySelectorAll('.tesla-dual-content__cta').forEach(fitButton);
    });
  }

  // ── Section: Charging Network / Simulator Finder ───────────────────────

  function processSimulatorFinder() {
    var c = CONFIG.finder;

    // Stats
    qsa('.stat-item').forEach(function(stat) {
      var num = stat.querySelector('.stat-item__number, .stat-number');
      var label = stat.querySelector('.stat-item__label, .stat-label');
      var sc = CONFIG.stats;

      if (num) {
        var size = fitText(num.textContent.trim(), num, sc.number.min, sc.number.max, sc.number.lh, 1);
        if (size) applySize(num, size);
      }
      if (label) {
        var size = fitText(label.textContent.trim(), label, sc.label.min, sc.label.max, sc.label.lh, 1);
        if (size) applySize(label, size);
      }
    });

    // Main heading + description
    var h = qs('.simulator-finder__heading');
    var d = qs('.simulator-finder__description');

    if (h) {
      var size = fitText(h.textContent.trim(), h, c.heading.min, c.heading.max, c.heading.lh, maxLinesFor(h, 3, 2));
      if (size) applySize(h, size);
    }
    if (d) {
      var size = fitText(d.textContent.trim(), d, c.desc.min, c.desc.max, c.desc.lh, maxLinesFor(d, 12, 6));
      if (size) applySize(d, size);
    }

    // CTA buttons
    qsa('.simulator-finder__ctas .btn').forEach(fitButton);
  }

  // ── Section: Event Rental CTA Banner ───────────────────────────────────

  function processEventRental() {
    var c = CONFIG.rental;
    var h = qs('.event-rental-hero__heading');
    var d = qs('.event-rental-hero__desc');

    if (h) {
      var size = fitText(h.textContent.trim(), h, c.heading.min, c.heading.max, c.heading.lh, maxLinesFor(h, 2, 1));
      if (size) applySize(h, size);
    }
    if (d) {
      var size = fitText(d.textContent.trim(), d, c.desc.min, c.desc.max, c.desc.lh, maxLinesFor(d, 5, 3));
      if (size) applySize(d, size);
    }

    qsa('.event-rental-hero__btn').forEach(fitButton);
  }

  // ── Section: TrackPro Promo ────────────────────────────────────────────

  function processTrackPro() {
    var section = qs('[class*="trackpro-promo"]');
    if (!section) return;

    section.querySelectorAll('h2, h3').forEach(function(h) {
      var text = h.textContent.trim();
      if (!text) return;
      var size = fitText(text, h, 18, 36, 1.2, maxLinesFor(h, 2, 1));
      if (size) applySize(h, size);
    });
    section.querySelectorAll('a[class*="btn"], button').forEach(fitButton);
  }

  // ── Section: Homepage SEO Content ──────────────────────────────────────

  function processSEOContent() {
    var c = CONFIG.seo;

    // Main section heading
    var mainH = qs('.homepage-seo-content__heading');
    if (mainH) {
      var size = fitText(mainH.textContent.trim(), mainH, c.heading.min, c.heading.max, c.heading.lh, maxLinesFor(mainH, 2, 1));
      if (size) applySize(mainH, size);
    }

    // Cards
    qsa('.homepage-seo-content__card').forEach(function(card) {
      var title = card.querySelector('.homepage-seo-content__card-title');
      var text = card.querySelector('.homepage-seo-content__card-text');

      if (title) {
        var size = fitText(title.textContent.trim(), title, c.title.min, c.title.max, c.title.lh, 1);
        if (size) applySize(title, size);
      }
      if (text) {
        var size = fitText(text.textContent.trim(), text, c.text.min, c.text.max, c.text.lh, maxLinesFor(text, 10, 6));
        if (size) applySize(text, size);
      }
    });
  }

  // ── Main ───────────────────────────────────────────────────────────────

  function processAll() {
    if (window.Pretext.clearCache) window.Pretext.clearCache();
    processHero();
    processCarousels();
    processDualContent();
    processSimulatorFinder();
    processTrackPro();
    processEventRental();
    processSEOContent();
  }

  // ── Resize Handler ─────────────────────────────────────────────────────

  var resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      // Reset all fitted sizes before re-measuring
      qsa('[data-pretext-fitted]').forEach(function(el) {
        el.style.fontSize = '';
        el.removeAttribute('data-pretext-fitted');
      });
      processAll();
    }, CONFIG.resizeDebounce);
  }

  // ── Init ───────────────────────────────────────────────────────────────

  function init() {
    if (!window.Pretext || !window.Pretext.prepare) {
      console.warn('[Pretext] Library not loaded, skipping text optimization');
      return;
    }

    // Smooth transition for size changes
    var style = document.createElement('style');
    style.textContent = '[data-pretext-fitted] { transition: font-size 0.15s ease-out; }';
    document.head.appendChild(style);

    processAll();
    window.addEventListener('resize', onResize);

    // Re-run when carousel slides change
    document.addEventListener('slideChange', function() { setTimeout(processAll, 50); });

    console.log('[Pretext] Homepage text optimization active — all sections');
  }

  // Wait for DOM + web fonts to be ready
  function boot() {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(init);
    } else {
      setTimeout(init, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
